'use client';

import { Box, Container, Typography } from '@mui/material';
import Link from 'next/link';
import CookieActions from './components/CookieActions';
import CookiePreference from './components/CookiePreference';


export default function CookiesEn({ settings, handleCookieChange, handleSaveSettings, handleRejectAll, notification }: I18nCookiesProps) {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Cookie Settings
        </Typography>
      </Box>

      {/* Introduction */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          We use cookies on RightPulse to improve your experience, analyze traffic, and personalize content and ads. By clicking "Accept All Cookies," you agree to our use of cookies as described in our Privacy Policy and Cookie Policy. However, you can manage your cookie settings below.
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          Manage Your Cookie Preferences
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          We use different types of cookies on our platform. Please select the types of cookies you would like to allow:
        </Typography>
      </Box>

      {/* Cookie Types */}
      <Box sx={{ mb: 4, pl: 4 }}>
        {/* Strictly Necessary Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            1. Strictly Necessary Cookies
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            These cookies are essential for the website to function properly and cannot be turned off. They are usually set in response to actions you take (like logging in or filling out forms).
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            Always Active — You cannot disable these cookies.
          </Typography>
        </Box>

        {/* Performance Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            2. Performance and Analytics Cookies
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            These cookies help us understand how visitors interact with our website, such as the pages you visit most often. We use this information to improve our website and services.
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Google Analytics</li>
            <li>Mixpanel</li>
            <li>Other analytics tools</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            You can choose to disable or enable these cookies at any time.
          </Typography>
          <CookiePreference
            label="Enable Performance Cookies"
            type="performance"
            initialValue={settings.performance}
            onChange={handleCookieChange}
          />
        </Box>

        {/* Functional Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            3. Functional Cookies
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            These cookies allow us to remember your preferences (like language or region) and provide enhanced, more personalized features.
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Language preferences</li>
            <li>Login details</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            You can choose to disable or enable these cookies at any time.
          </Typography>
          <CookiePreference
            label="Enable Functional Cookies"
            type="functional"
            initialValue={settings.functional}
            onChange={handleCookieChange}
          />
        </Box>

        {/* Advertising Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            4. Advertising and Targeting Cookies
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            These cookies are used to deliver personalized ads based on your browsing habits. They are also used to limit the number of times you see an advertisement and help us measure the effectiveness of our marketing campaigns.
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Google AdSense</li>
            <li>Facebook Pixel</li>
            <li>Other ad networks</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            You can choose to disable or enable these cookies at any time.
          </Typography>
          <CookiePreference
            label="Enable Advertising Cookies"
            type="advertising"
            initialValue={settings.advertising}
            onChange={handleCookieChange}
          />
        </Box>
      </Box>

      {/* Additional Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          How to Manage Your Cookie Settings
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          You can update your preferences at any time by visiting our Cookie Settings page. If you disable certain cookies, some parts of the website may not function as expected.
        </Typography>
      </Box>

      {/* Consent */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Consent
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          If you prefer to limit the types of cookies we use, select your preferences above and click "Confirm"
        </Typography>
      </Box>

      {/* Learn More */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Learn More
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          If you want to know more about how we protect your privacy, please check out our{' '}
          <Link
            href="/legal/privacy"
            style={{
              color: 'primary.main',
              textDecoration: 'underline',
            }}
          >
            Privacy Policy
          </Link>
          .
        </Typography>
      </Box>

      {/* Action Buttons */}
      <CookieActions
        onReject={handleRejectAll}
        onSave={handleSaveSettings}
      />

      {/* Notification */}
      {notification}
    </Container>
  );
}
